<template>
	<v-menu
		v-model="menuOpen"
		offset-y
		:nudge-bottom="6"
		:min-width="menuWidth"
		:max-width="320"
		:close-on-content-click="false"
		content-class="portal-select-menu"
	>
		<template v-slot:activator="{ on, attrs }">
			<button
				ref="trigger"
				type="button"
				:class="['portal-select-field', fieldClass, { 'is-open': menuOpen }]"
				:aria-label="label"
				:aria-expanded="menuOpen ? 'true' : 'false'"
				aria-haspopup="listbox"
				v-bind="attrs"
				v-on="on"
			>
				<span class="select-label">{{ label }}</span>
				<span class="select-value">{{ selectedLabel }}</span>
				<portal-icon
					name="chevron-down"
					:size="16"
					aria-hidden="true"
					class="select-chevron"
				></portal-icon>
			</button>
		</template>
		<div class="portal-select-options" role="listbox" :aria-label="label">
			<button
				v-for="option in options"
				:key="String(option.value)"
				type="button"
				class="portal-select-option"
				:class="{ 'is-selected': isSelected(option) }"
				role="option"
				:aria-selected="isSelected(option) ? 'true' : 'false'"
				@click="selectOption(option)"
			>
				<span>{{ option.text }}</span>
				<portal-icon
					v-if="isSelected(option)"
					name="check"
					:size="16"
					aria-hidden="true"
				></portal-icon>
			</button>
		</div>
	</v-menu>
</template>

<script>
	export default {
		name: 'PortalSelectField',

		props: {
			value: { type: [String, Number, Boolean], required: false, default: '' },
			label: { type: String, required: true },
			options: { type: Array, required: true },
			fieldClass: { type: String, required: false, default: '' },
		},

		data() {
			return {
				menuOpen: false,
				menuWidth: 220,
			};
		},

		computed: {
			normalizedValue() {
				return this.value === null || typeof this.value === 'undefined' ? '' : this.value;
			},

			selectedLabel() {
				const selected = this.options.find((option) => this.isSelected(option));
				return selected ? selected.text : '';
			},
		},

		methods: {
			isSelected(option) {
				return String(option.value) === String(this.normalizedValue);
			},

			selectOption(option) {
				this.$emit('input', option.value);
				this.$emit('change', option.value);
				this.menuOpen = false;
				this.$nextTick(() => this.$refs.trigger && this.$refs.trigger.focus());
			},
		},

		watch: {
			menuOpen(open) {
				if (open && this.$refs.trigger) {
					this.menuWidth = Math.max(220, this.$refs.trigger.offsetWidth);
				}
			},
		},
	};
</script>

<style scoped lang="scss">
	.portal-select-field {
		height: 42px;
		min-width: 174px;
		flex: 0 1 220px;
		display: flex;
		align-items: center;
		gap: 8px;
		padding: 0 10px 0 12px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-field-radius, 12px);
		background: var(--brand-field-fill, var(--portal-canvas));
		color: var(--brand-surfaceFieldAnswer, var(--portal-text));
		font: inherit;
		text-align: left;
		cursor: pointer;
		transition: border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease;
	}

	.portal-select-field:hover {
		border-color: color-mix(in srgb, var(--portal-muted) 55%, var(--portal-border));
		background: var(--brand-field-fill, var(--portal-surface));
	}

	.portal-select-field:focus-visible,
	.portal-select-field.is-open {
		outline: 0;
		border-color: var(--v-primary-base);
		background: var(--brand-field-fill, var(--portal-surface));
		box-shadow: 0 0 0 3px color-mix(in srgb, var(--v-primary-base) 12%, transparent);
	}

	.select-label {
		flex: 0 0 auto;
		color: var(--brand-surfaceFieldPlaceholder, var(--portal-muted));
		font-size: 12px;
		font-weight: 600;
	}

	.select-value {
		min-width: 0;
		flex: 1;
		color: var(--brand-surfaceFieldAnswer, var(--portal-text));
		font-size: 14px;
		font-weight: 550;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.select-chevron {
		flex: 0 0 auto;
		color: var(--brand-surfaceFieldPlaceholder, var(--portal-muted));
		transition: transform 140ms ease;
	}

	.is-open .select-chevron {
		transform: rotate(180deg);
	}

	@media (max-width: 599px) {
		.portal-select-field {
			width: 100%;
			min-width: 0;
			flex-basis: auto;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.portal-select-field,
		.select-chevron {
			transition: none;
		}
	}
</style>

<style lang="scss">
	.portal-select-menu {
		padding: 6px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 14px) !important;
		background: var(--portal-surface);
		box-shadow: 0 14px 38px rgba(17, 24, 39, 0.14) !important;
	}

	.portal-select-options {
		max-height: min(320px, 55vh);
		overflow-y: auto;
	}

	.portal-select-option {
		width: 100%;
		min-height: 40px;
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 16px;
		padding: 8px 10px;
		border: 0;
		border-radius: var(--brand-button-radius, 9px);
		background: transparent;
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 550;
		text-align: left;
		cursor: pointer;
	}

	.portal-select-option:hover,
	.portal-select-option:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
	}

	.portal-select-option.is-selected {
		background: color-mix(in srgb, var(--v-primary-base) 9%, var(--portal-surface));
		color: var(--v-primary-base);
	}
	</style>
